import {SourcesService} from '../../core/services/sources/SourcesService'
import {sourcesClient} from '../../core/services/sources/sourcesClient'
import {IFeatureLayerInfos, IUser, IFieldInfo} from '../../core/interfaces/reducers'
import appConfig from '../../reducers/appConfig'
import {IFeatureLayerInfo, IRawSourceDescriptions, ISourcesInfosPayload} from '../../core/interfaces/models'
import {DeniedServiceInfo, Extent, FailedServiceInfo, FeatureLayerInfo} from '../../core/models'
import {getMapService} from '../../core/reducers/map'
import {ViewSchemaType} from '../../core/enums/ViewSchemaType'

export class AppSourcesService extends SourcesService {
  protected resolveInfoPayload(
    allInfos: (IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo)[],
    resolve: (value: ISourcesInfosPayload) => void,
    user?: IUser
  ) {
    const featureLayerInfos: IFeatureLayerInfos = {}
    const failedServices: {[key: string]: FailedServiceInfo} = {}
    const deniedServices: {[key: string]: DeniedServiceInfo} = {}
    allInfos.forEach(layerInfo => {
      if (layerInfo instanceof FailedServiceInfo) {
        failedServices[layerInfo.url] = layerInfo
      } else if (layerInfo instanceof DeniedServiceInfo) {
        deniedServices[layerInfo.url] = layerInfo
      } else if (layerInfo instanceof FeatureLayerInfo) {
        featureLayerInfos[layerInfo.url] = layerInfo
      }
    })
    this.applyNomenclatures(featureLayerInfos, user).then(result => {
      this.gatherLegendInfos(allInfos).then(() => {
        resolve({
          featureLayerInfos: result,
          failedServices,
          deniedServices
        })
      })
    })
  }

  protected applyNomenclatures(infos: IFeatureLayerInfos, user?: IUser): Promise<IFeatureLayerInfos> {
    const pushNomenclatures = (nom: {Id: string; Name: string}[], field: IFieldInfo) => {
      field.codedValues = []
      nom.forEach(n => {
        field.codedValues!.push({
          code: n.Id,
          name: n.Name
        })
      })
    }
    return sourcesClient.getNomenclatures(appConfig.nomenclaturesUrl).then(data => {
      for (const key in infos) {
        const i = infos[key]
        for (const fieldName in i.fields) {
          if (data[fieldName]) {
            pushNomenclatures(data[fieldName], i.fields[fieldName])
          } else if (data[fieldName.replace(/Id/g, '')]) {
            pushNomenclatures(data[fieldName.replace(/Id/g, '')], i.fields[fieldName])
          }
        }
      }
      return infos
    })
  }

  protected getConfigRawSourceDescriptions(user?: IUser): Promise<IRawSourceDescriptions> {
    return new Promise((resolve, reject) => {
      let sources = {} as IRawSourceDescriptions
      //TODO: Maybe remove duplicate code when I'm sure its possible
      if (appConfig.admin) {
        if (user) {
          sources = appConfig.sourcesDescription
        } else {
          throw new Error('User must login')
        }
      } else {
        sources[appConfig.urls.xyApiUrl] = {
          serviceType: appConfig.sourcesDescription[appConfig.urls.xyApiUrl].serviceType,
          namespaces: {}
        }
        if (appConfig.isCad) {
          sources[appConfig.urls.geoserverApiUrl] = appConfig.sourcesDescription[appConfig.urls.geoserverApiUrl]
          for (const namesp in sources[appConfig.urls.geoserverApiUrl].namespaces) {
            for (const gr in sources[appConfig.urls.geoserverApiUrl].namespaces[namesp]) {
              for (const layer in sources[appConfig.urls.geoserverApiUrl].namespaces[namesp][gr].layers) {
                sources[appConfig.urls.geoserverApiUrl].namespaces[namesp][gr].layers[layer].visible = true
              }
            }
          }
        }
        if (appConfig.isReg || appConfig.isArchImport) {
          sources[appConfig.urls.xyApiUrl].namespaces['Reg'] =
            appConfig.sourcesDescription[appConfig.urls.xyApiUrl].namespaces['Reg']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['zones']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['zonelines']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['reg'].layers['RegOsLine']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['reg'].layers['RegLine']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['reg'].layers['RegPoint']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['reg'].layers['StreetUnregulatedReg']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['reg'].layers['PlanPrintCoordinate']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['reg'].layers['Perpendiculars']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['reg'].layers['Measures']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['reg'].layers['cadPreviewLine']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['reg'].layers['cadPreviewContur']
          delete sources[appConfig.urls.xyApiUrl].namespaces['Reg']['reg'].layers['cadPreviewText']
        }
        if (appConfig.isUnd) {
          sources[appConfig.urls.xyApiUrl].namespaces['UND'] =
            appConfig.sourcesDescription[appConfig.urls.xyApiUrl].namespaces['UND']
        }
        if (appConfig.isArchImport) {
          sources[appConfig.urls.xyApiUrl].namespaces['ArchImport'] =
            appConfig.sourcesDescription[appConfig.urls.xyApiUrl].namespaces['ArchImport']
        }
        if (appConfig.isOUP) {
          sources[appConfig.urls.xyApiUrl].namespaces['OUP'] =
            appConfig.sourcesDescription[appConfig.urls.xyApiUrl].namespaces['OUP']
        }
        for (const namespace in sources[appConfig.urls.xyApiUrl].namespaces) {
          for (const group in sources[appConfig.urls.xyApiUrl].namespaces[namespace]) {
            for (const id in sources[appConfig.urls.xyApiUrl].namespaces[namespace][group].layers) {
              const layer = sources[appConfig.urls.xyApiUrl].namespaces[namespace][group].layers[id]
              if (layer.fieldInfos['Created'] && layer.fieldInfos['Modified']) {
                layer.fieldInfos['Created'].visible = false
                layer.fieldInfos['Modified'].visible = false
              }

              if (namespace == 'Reg') {
                if (id == 'Parcel' || id == 'Quarter') {
                  layer.pseudoViewSchema = {0: ViewSchemaType.Image}
                }
                if (id == 'RegPlan') {
                  delete sources[appConfig.urls.xyApiUrl].namespaces[namespace][group].layers[id]
                } else {
                  for (const zoom in layer.pseudoViewSchema) {
                    if (layer.pseudoViewSchema[zoom] == ViewSchemaType.Vector) {
                      layer.pseudoViewSchema[zoom] = ViewSchemaType.Image
                    }
                  }
                }
              }
              if (namespace == 'OUP') {
                layer.pseudoViewSchema = {0: ViewSchemaType.Image}
              }
            }
          }
        }
      }
      resolve(sources)
    })
  }
}
